"use client";

import { useState } from "react";
import type { OAuthProvider } from "@/lib/api/oauth";
import type { OfferedMethods, TelegramBot } from "@/lib/api/signin-methods";
import { describeAccountUnavailable, refusalFromError, type SignInRefusal } from "@/lib/api/signin-outcome";
import { useOAuthSignIn, useTelegramSignIn } from "@/lib/api/social";
import { useTelegramLogin } from "@/lib/telegram/login";
import { Icon } from "@/components/ui/Icon";

/* The sign-in dialog's Telegram, Google and X buttons — the whitelabel's three
   (batman `RegisterModal`), drawn as this dialog's own buttons. Each is shown
   only when `GET /signin` offers it (lib/api/signin-methods.ts).

   - Telegram opens Telegram's own confirmation popup (lib/telegram/login.ts)
     and posts the signed user to `POST /signin/telegram/verify`.
   - Google and X open a popup on the API's host, which reports back by
     postMessage (lib/api/social.ts, lib/api/oauth.ts).

   All three carry the held referral, and retry without it on a referral
   conflict. */

const BUTTON =
  "flex h-[46px] w-full items-center justify-center gap-[8px] rounded-lg bg-elevation-2 px-[12px] text-[14px] font-bold leading-[1.6] capitalize tracking-[0.28px] text-fg transition-colors hover:bg-elevation-3 disabled:opacity-40";

export function SocialSignIn({
  offered,
  active,
  onSignedIn,
}: {
  offered: OfferedMethods;
  /* The dialog is open: the Telegram script is loaded ahead of the click. */
  active: boolean;
  onSignedIn: () => void;
}) {
  return (
    <div className="flex flex-col gap-[8px]">
      {offered.telegram && <TelegramButton bot={offered.telegram} active={active} onSignedIn={onSignedIn} />}
      {offered.google && <OAuthButton provider="google" onSignedIn={onSignedIn} />}
      {offered.twitter && <OAuthButton provider="twitter" onSignedIn={onSignedIn} />}
    </div>
  );
}

const PROVIDER_LABEL: Record<OAuthProvider, string> = { google: "Google", twitter: "X" };

function OAuthButton({ provider, onSignedIn }: { provider: OAuthProvider; onSignedIn: () => void }) {
  const { state, start } = useOAuthSignIn(provider, onSignedIn);
  const label = PROVIDER_LABEL[provider];

  return (
    <>
      <button type="button" className={BUTTON} disabled={state.status === "pending"} onClick={start}>
        {provider === "google" ? <GoogleMark /> : <Icon name="x" size={16} />}
        {state.status === "pending" ? `Continue in the ${label} window…` : `Continue with ${label}`}
      </button>
      {state.status === "refused" && <RefusalNotice refusal={state.refusal} method={label} />}
    </>
  );
}

function TelegramButton({
  bot,
  active,
  onSignedIn,
}: {
  bot: TelegramBot;
  active: boolean;
  onSignedIn: () => void;
}) {
  const login = useTelegramLogin(active);
  const signIn = useTelegramSignIn();
  const [waiting, setWaiting] = useState(false);

  /* Not disabled while Telegram's window is up: a popup the browser blocked never
     calls back, and the player must be able to click again. */
  const busy = signIn.isPending;

  return (
    <>
      <button
        type="button"
        className={BUTTON}
        disabled={!login.ready || busy}
        onClick={() => {
          signIn.reset();
          setWaiting(true);
          /* Called inside the click, so Telegram's popup is a user gesture. */
          void login.authenticate(bot.botId).then((user) => {
            setWaiting(false);

            if (user) {
              signIn.mutate(user, { onSuccess: onSignedIn });
            }
          });
        }}
      >
        <Icon name="telegram" size={16} className="text-[#2AABEE]" />
        {signIn.isPending ? "Signing in…" : waiting ? "Continue in the Telegram window…" : "Continue with Telegram"}
      </button>
      {login.failed && (
        <Notice>Telegram did not load. An ad or tracker blocker may be stopping it; allow telegram.org and reopen.</Notice>
      )}
      {signIn.isError && <RefusalNotice refusal={refusalFromError(signIn.error)} method="Telegram" />}
    </>
  );
}

export function RefusalNotice({ refusal, method }: { refusal: SignInRefusal; method: string }) {
  switch (refusal.kind) {
    case "geoblocked":
      return (
        <Notice>
          Sign-in isn&rsquo;t available from your region. This comes from the platform&rsquo;s own
          geoblocking, not from this site.
        </Notice>
      );
    case "account-unavailable":
      return <Notice>{describeAccountUnavailable(refusal.account)}</Notice>;
    case "not-allowed":
      return <Notice>{method} sign-in is not set up for this site yet.</Notice>;
    case "interrupted":
      return (
        <Notice>
          The {method} window was blocked or closed before sign-in finished. Allow pop-ups for this site and
          try again.
        </Notice>
      );
    default:
      return <Notice>Could not finish signing in with {method}. Try again.</Notice>;
  }
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="rounded-lg bg-elevation-2 px-[12px] py-[8px] text-[12px] leading-[1.6] text-error-fg">
      {children}
    </p>
  );
}

/* Google's mark in its own colours, as its branding asks. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" width={16} height={16} aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
